import { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import { fetchServices } from "../../api/services";
import { createCustomer, searchCustomers as apiSearchCustomers } from "../../api/customers";
import { createBooking } from "../../api/bookings";
import { toISO } from "../../utils/dates";
import { formatUSD } from "../../utils/money";
import { formatEasternTime } from "../../utils/dates";
import { TaxCountyPicker } from "../common/TaxCountyPicker";
import VenueAutocomplete from "../common/VenueAutocomplete";

// Real customer search using your API
async function searchCustomers(query) {
  try {
    const results = await apiSearchCustomers(query, 10);
    return Array.isArray(results) ? results : [];
  } catch (error) {
    console.error("Customer search failed:", error);
    return [];
  }
}

function clampInt(n, fallback = 1) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.trunc(x);
}

function isHalfHourIncrement(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  return date.getSeconds() === 0 && date.getMilliseconds() === 0 && (date.getMinutes() === 0 || date.getMinutes() === 30);
}

function roundToNearestHalfHour(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const mod = minutes % 30;
  if (mod === 0) return d;
  if (mod < 15) d.setMinutes(minutes - mod);
  else d.setMinutes(minutes + (30 - mod));
  return d;
}

function floorToHalfHour(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  d.setMinutes(minutes - (minutes % 30));
  return d;
}

function ceilToHalfHour(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const mod = minutes % 30;
  if (mod === 0) return d;
  d.setMinutes(minutes + (30 - mod));
  return d;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addMinutes(date, mins) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() + mins);
  return d;
}

function formatSlotLabel(d) {
  // Render time slots in Eastern Time (the business's operating zone)
  return formatEasternTime(d);
}

function toDateInputValue(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fromDateInputValue(v) {
  if (!v) return new Date();
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date();
  dt.setFullYear(y, (m || 1) - 1, d || 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export default function EnhancedCreateBookingModal({ isOpen, onClose, defaultStart }) {
  // Services
  const [loadingServices, setLoadingServices] = useState(false);
  const [services, setServices] = useState([]);

  // Form state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // 1: Customer, 2: Booking Details

  // Customer selection
  const [customerMode, setCustomerMode] = useState("search"); // "search" or "new"
  const [customerSearch, setCustomerSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // New customer fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [venueName, setVenueName] = useState("");
  const [customerType, setCustomerType] = useState("individual"); // individual, business, venue, corporate

  // Booking fields
  const [serviceId, setServiceId] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => toDateInputValue(defaultStart || new Date()));
  const [startSlot, setStartSlot] = useState(() => {
    const base = defaultStart || new Date();
    return floorToHalfHour(base).toISOString();
  });
  const [units, setUnits] = useState(1);

  // Location
  const [locationAddress, setLocationAddress] = useState("");
  const [locationNotes, setLocationNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  // Pricing customization
  const [pricingMode, setPricingMode] = useState("standard"); // "standard", "custom", "discount"
  const [customPrice, setCustomPrice] = useState("");
  const [discountType, setDiscountType] = useState("percentage"); // "percentage" or "fixed"
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");

  // Sales tax
  const [taxCounty, setTaxCounty] = useState("");
  const [taxRateBasisPoints, setTaxRateBasisPoints] = useState(null);
  const [taxAutoDetected, setTaxAutoDetected] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Reset form
    setError("");
    setSubmitting(false);
    setStep(1);
    setCustomerMode("search");
    setCustomerSearch("");
    setSearchResults([]);
    setSelectedCustomer(null);
    
    setFullName("");
    setEmail("");
    setPhone("");
    setCompanyName("");
    setVenueName("");
    setCustomerType("individual");
    setLocationAddress("");
    setLocationNotes("");
    setInternalNotes("");
    
    setPricingMode("standard");
    setCustomPrice("");
    setDiscountValue("");
    setDiscountReason("");

    const base = defaultStart || new Date();
    const rounded = roundToNearestHalfHour(base);
    setSelectedDate(toDateInputValue(rounded));
    setStartSlot(rounded.toISOString());
    setUnits(1);

    // Load services
    (async () => {
      setLoadingServices(true);
      try {
        const data = await fetchServices();
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
        setServices(list);
        setServiceId(list[0] ? String(list[0].id) : "");
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingServices(false);
      }
    })();
  }, [isOpen, defaultStart]);

  // Customer search with debouncing
  useEffect(() => {
    if (!customerSearch.trim() || customerMode !== "search") {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchCustomers(customerSearch);
        setSearchResults(results);
      } catch (e) {
        console.error("Customer search failed:", e);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearch, customerMode]);

  const selectedService = useMemo(() => {
    return services.find((s) => String(s.id) === String(serviceId)) || null;
  }, [services, serviceId]);

  const showUnits = useMemo(() => {
    if (!selectedService) return false;
    return (
      selectedService.pricing_mode === "per_unit" ||
      selectedService.service_type === "multi_day" ||
      selectedService.service_type === "recurring"
    );
  }, [selectedService]);

  const unitLabel = selectedService?.unit_label || "units";
  const minUnits = selectedService?.min_units ?? 1;
  const maxUnits = selectedService?.max_units ?? null;
  const unitsInt = useMemo(() => clampInt(units, 1), [units]);

  // Enhanced pricing calculation
  const pricingCalculation = useMemo(() => {
    if (!selectedService) return { baseCents: 0, finalCents: 0, discountCents: 0, breakdown: "" };

    let baseCents = 0;
    if (selectedService.pricing_mode === "per_unit") {
      const ppu = Number(selectedService.price_per_unit_cents || 0);
      baseCents = ppu * (showUnits ? unitsInt : 1);
    } else {
      baseCents = Number(selectedService.price_cents || 0);
    }

    let finalCents = baseCents;
    let discountCents = 0;
    let breakdown = "";

    if (pricingMode === "custom" && customPrice) {
      finalCents = Math.round(Number(customPrice) * 100);
      discountCents = baseCents - finalCents;
      breakdown = "Custom pricing";
    } else if (pricingMode === "discount" && discountValue) {
      const discVal = Number(discountValue);
      if (discountType === "percentage") {
        discountCents = Math.round(baseCents * (discVal / 100));
        breakdown = `${discVal}% discount`;
      } else {
        discountCents = Math.round(discVal * 100);
        breakdown = `$${discVal} discount`;
      }
      finalCents = baseCents - discountCents;
    }

    return { baseCents, finalCents, discountCents, breakdown };
  }, [selectedService, unitsInt, showUnits, pricingMode, customPrice, discountType, discountValue]);

  const startLocal = useMemo(() => {
    const day = fromDateInputValue(selectedDate);
    const slot = new Date(startSlot);
    if (Number.isNaN(day.getTime()) || Number.isNaN(slot.getTime())) return new Date();

    const merged = new Date(day);
    merged.setHours(slot.getHours(), slot.getMinutes(), 0, 0);
    return merged;
  }, [selectedDate, startSlot]);

  const slotOptions = useMemo(() => {
    const day = fromDateInputValue(selectedDate);
    if (Number.isNaN(day.getTime())) return [];

    const start = addMinutes(startOfDay(day), 6 * 60); // 6 AM
    const end = addMinutes(startOfDay(day), 22 * 60); // 10 PM

    const opts = [];
    let cur = new Date(start);
    cur = ceilToHalfHour(cur);

    while (cur <= end) {
      opts.push({
        iso: cur.toISOString(),
        label: formatSlotLabel(cur),
      });
      cur = addMinutes(cur, 30);
    }
    return opts;
  }, [selectedDate]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFullName(customer.full_name);
    setEmail(customer.email || "");
    setPhone(customer.phone || "");
    setCompanyName(customer.company_name || "");
    setVenueName(customer.venue_name || "");
    setCustomerType(customer.customer_type || "individual");
    setStep(2);
  };

  const handleNewCustomer = () => {
    setSelectedCustomer(null);
    setStep(2);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    setError("");

    // Extra safety check - don't submit if already submitting
    if (submitting) {
      return;
    }

    if (!selectedService) {
      setError("Please select a service.");
      return;
    }

    const name = (fullName || "").trim();
    if (!name) {
      setError("Customer full name is required.");
      return;
    }

    const start = startLocal instanceof Date ? startLocal : new Date(startLocal);
    if (Number.isNaN(start.getTime())) {
      setError("Invalid start time.");
      return;
    }

    if (!isHalfHourIncrement(start)) {
      setError("Start time must be on a 30-minute boundary.");
      return;
    }

    const u = showUnits ? clampInt(unitsInt, 1) : 1;
    if (showUnits) {
      if (u < minUnits) {
        setError(`Units must be >= ${minUnits}.`);
        return;
      }
      if (maxUnits != null && u > Number(maxUnits)) {
        setError(`Units must be <= ${Number(maxUnits)}.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      let customerId;
      
      if (selectedCustomer) {
        customerId = selectedCustomer.id;
      } else {
        const cust = await createCustomer({
          full_name: name,
          email: (email || "").trim() || null,
          phone: (phone || "").trim() || null,
          company_name: (companyName || "").trim() || null,
        });
        customerId = cust.id;
      }

      // Prepare pricing data
      const pricingData = {};
      if (pricingMode === "custom") {
        pricingData.custom_price_cents = pricingCalculation.finalCents;
      } else if (pricingMode === "discount") {
        pricingData.custom_price_cents = pricingCalculation.finalCents;
        pricingData.discount_amount_cents = pricingCalculation.discountCents;
        pricingData.discount_reason = discountReason.trim() || pricingCalculation.breakdown;
      }

      const created = await createBooking({
        service_id: Number(selectedService.id),
        customer_id: Number(customerId),
        start_at: toISO(start),
        units: u,
        resource_id: "birdie_bus_1",
        location_address: locationAddress.trim() || null,
        location_notes: locationNotes.trim() || null,
        internal_notes: internalNotes.trim() || null,
        // Enhanced booking fields
        custom_price_cents: pricingData.custom_price_cents || null,
        discount_amount_cents: pricingData.discount_amount_cents || null,
        discount_reason: pricingData.discount_reason || null,
        tax_county: taxCounty || null,
        tax_rate_basis_points: taxRateBasisPoints,
        booking_source: "admin", // "admin", "venue_portal", "customer_portal"
        created_by: "admin_user", // This would be the actual user ID in a real app
        venue_id: null, // For future venue owner bookings
      });

      onClose?.({ didCreate: true, booking: created });
    } catch (e2) {
      setError(e2.message || "Failed to create booking.");
      setSubmitting(false);
    }
  }

  if (loadingServices) {
    return (
      <Modal isOpen={isOpen} title="Create Booking" onClose={() => onClose?.({ didCreate: false })}>
        <Spinner label="Loading services..." />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      title="Create Booking"
      onClose={() => onClose?.({ didCreate: false })}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-6">
        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {/* Step Indicator */}
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
            step === 1 ? 'bg-birdie-green/10 text-birdie-green' : 'bg-birdie-beige text-birdie-charcoal/70'
          }`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              step === 1 ? 'bg-birdie-green text-white' : 'bg-birdie-warmgray text-white'
            }`}>1</span>
            Customer
          </div>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
            step === 2 ? 'bg-birdie-green/10 text-birdie-green' : 'bg-birdie-beige text-birdie-charcoal/70'
          }`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              step === 2 ? 'bg-birdie-green text-white' : 'bg-birdie-warmgray text-white'
            }`}>2</span>
            Booking Details
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            {/* Customer Mode Toggle */}
            <div className="flex gap-1 rounded-lg bg-birdie-beige p-1 w-fit">
              <Button
                type="button"
                size="sm"
                variant={customerMode === "search" ? "primary" : "ghost"}
                onClick={() => setCustomerMode("search")}
                className="px-4 py-2"
              >
                🔍 Find Existing Customer
              </Button>
              <Button
                type="button"
                size="sm"
                variant={customerMode === "new" ? "primary" : "ghost"}
                onClick={() => setCustomerMode("new")}
                className="px-4 py-2"
              >
                ➕ New Customer
              </Button>
            </div>

            {customerMode === "search" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Search customers</label>
                  <input
                    className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by name, email, or phone..."
                  />
                </div>

                {searching && (
                  <div className="flex items-center justify-center py-4">
                    <Spinner label="Searching..." />
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Found {searchResults.length} customers:</div>
                    {searchResults.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        className="flex items-center justify-between p-4 border border-birdie-green/15 rounded-xl hover:bg-birdie-cream cursor-pointer transition-colors"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-lg text-birdie-charcoal">{customer.full_name}</div>
                          {(customer.company_name || customer.venue_name) && (
                            <div className="font-semibold text-birdie-green text-sm">
                              {customer.company_name && `🏢 ${customer.company_name}`}
                              {customer.venue_name && `🏛️ ${customer.venue_name}`}
                            </div>
                          )}
                          <div className="text-sm text-birdie-charcoal/70">{customer.email} • {customer.phone}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              customer.customer_type === 'venue' ? 'bg-birdie-green/10 text-birdie-green' :
                              customer.customer_type === 'corporate' ? 'bg-birdie-green/10 text-birdie-green' :
                              customer.customer_type === 'business' ? 'bg-birdie-orange/20 text-birdie-orange-dark' :
                              'bg-birdie-beige text-birdie-charcoal/70'
                            }`}>
                              {customer.customer_type === 'venue' ? '🏛️ Venue' :
                               customer.customer_type === 'corporate' ? '🏢 Corporate' :
                               customer.customer_type === 'business' ? '💼 Business' :
                               '👤 Individual'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs bg-birdie-green/10 text-birdie-green px-3 py-1 rounded-full font-medium">
                            {customer.booking_count} bookings
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {customerSearch.trim() && !searching && searchResults.length === 0 && (
                  <div className="text-center py-6 text-birdie-charcoal/60">
                    <div className="text-2xl mb-2">😕</div>
                    <div>No customers found for "{customerSearch}"</div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setCustomerMode("new");
                        setFullName(customerSearch);
                      }}
                      className="mt-2"
                    >
                      Create as new customer
                    </Button>
                  </div>
                )}
              </div>
            )}

            {customerMode === "new" && (
              <div className="space-y-4">
                {/* Customer Type Selection */}
                <div>
                  <label className="text-sm font-medium">Customer Type *</label>
                  <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
                    {[
                      { value: "individual", label: "👤 Individual", desc: "Personal customer" },
                      { value: "business", label: "💼 Business", desc: "Event planner, etc." },
                      { value: "venue", label: "🏛️ Venue", desc: "Wedding venue, etc." },
                      { value: "corporate", label: "🏢 Corporate", desc: "Company events" }
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setCustomerType(type.value)}
                        className={`p-3 text-left border rounded-xl transition-colors text-sm ${
                          customerType === type.value
                            ? 'border-birdie-green bg-birdie-cream text-birdie-charcoal'
                            : 'border-birdie-green/15 hover:border-birdie-green/25'
                        }`}
                      >
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-birdie-charcoal/60">{type.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">Contact Name *</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone *</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                </div>

                {/* Company/Venue Fields */}
                {(customerType === "business" || customerType === "corporate") && (
                  <div>
                    <label className="text-sm font-medium">Company Name *</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="ACME Corporation"
                    />
                  </div>
                )}

                {customerType === "venue" && (
                  <div>
                    <label className="text-sm font-medium">Venue Name *</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={venueName}
                      onChange={(e) => setVenueName(e.target.value)}
                      placeholder="Grand Ballroom Events"
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Email (optional)</label>
                  <input
                    className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@email.com"
                  />
                </div>

                <Button
                  onClick={handleNewCustomer}
                  disabled={!fullName.trim() || 
                    (customerType === "business" && !companyName.trim()) ||
                    (customerType === "corporate" && !companyName.trim()) ||
                    (customerType === "venue" && !venueName.trim())
                  }
                  className="w-full"
                >
                  Continue with New Customer
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Selected Customer Info */}
            <div className="bg-birdie-cream rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-birdie-charcoal text-lg">{fullName}</div>
                  {(companyName || venueName) && (
                    <div className="font-semibold text-birdie-green mt-1">
                      {companyName && `🏢 ${companyName}`}
                      {venueName && `🏛️ ${venueName}`}
                    </div>
                  )}
                  <div className="text-sm text-birdie-charcoal/70 mt-1">{phone} • {email}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      customerType === 'venue' ? 'bg-birdie-green/10 text-birdie-green' :
                      customerType === 'corporate' ? 'bg-birdie-green/10 text-birdie-green' :
                      customerType === 'business' ? 'bg-birdie-orange/20 text-birdie-orange-dark' :
                      'bg-birdie-beige text-birdie-charcoal/70'
                    }`}>
                      {customerType === 'venue' ? '🏛️ Venue Customer' :
                       customerType === 'corporate' ? '🏢 Corporate Client' :
                       customerType === 'business' ? '💼 Business Client' :
                       '👤 Individual Customer'}
                    </span>
                    {selectedCustomer && (
                      <span className="text-xs bg-birdie-green/10 text-birdie-green px-2 py-1 rounded-full">
                        {selectedCustomer.booking_count} previous bookings
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setStep(1)}
                >
                  Change Customer
                </Button>
              </div>
            </div>

            {/* Service & DateTime */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Service *</label>
                <select
                  className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                >
                  {services.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name} - {formatUSD(Number(s.price_cents || 0))}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Date *</label>
                  <input
                    type="date"
                    className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Time *</label>
                  <select
                    className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                    value={startSlot}
                    onChange={(e) => setStartSlot(e.target.value)}
                  >
                    {slotOptions.map((opt) => (
                      <option key={opt.iso} value={opt.iso}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="border border-birdie-green/15 rounded-xl p-4 bg-birdie-beige">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-birdie-charcoal">Pricing</h3>
                <div className="flex gap-1 rounded-lg bg-white p-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={pricingMode === "standard" ? "primary" : "ghost"}
                    onClick={() => setPricingMode("standard")}
                    className="px-3 py-1 text-xs"
                  >
                    Standard
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={pricingMode === "discount" ? "primary" : "ghost"}
                    onClick={() => setPricingMode("discount")}
                    className="px-3 py-1 text-xs"
                  >
                    Discount
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={pricingMode === "custom" ? "primary" : "ghost"}
                    onClick={() => setPricingMode("custom")}
                    className="px-3 py-1 text-xs"
                  >
                    Custom
                  </Button>
                </div>
              </div>

              {pricingMode === "discount" && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 mb-4">
                  <div>
                    <label className="text-sm font-medium">Discount Type</label>
                    <select
                      className="mt-1 w-full rounded-lg border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {discountType === "percentage" ? "Percentage" : "Amount"}
                    </label>
                    <input
                      type="number"
                      className="mt-1 w-full rounded-lg border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === "percentage" ? "10" : "50"}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Reason</label>
                    <input
                      className="mt-1 w-full rounded-lg border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      placeholder="Repeat customer"
                    />
                  </div>
                </div>
              )}

              {pricingMode === "custom" && (
                <div className="mb-4">
                  <label className="text-sm font-medium">Custom Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 w-full rounded-lg border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    placeholder="250.00"
                  />
                </div>
              )}

              {/* Price Summary */}
              {(() => {
                const rateBp = taxRateBasisPoints ?? 0;
                const taxBase = Math.max(0, pricingCalculation.finalCents);
                const taxCents = Math.round((taxBase * rateBp) / 10000);
                const totalWithTax = pricingCalculation.finalCents + taxCents;
                const taxLabel =
                  taxCounty && rateBp
                    ? `Sales tax (${taxCounty}, ${(rateBp / 100).toFixed(3).replace(/\.?0+$/, "")}%):`
                    : "Sales tax:";
                return (
              <div className="bg-white rounded-lg p-4 border border-birdie-green/15">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Price:</span>
                    <span>{formatUSD(pricingCalculation.baseCents)}</span>
                  </div>
                  {pricingCalculation.discountCents > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Discount ({pricingCalculation.breakdown}):</span>
                      <span>-{formatUSD(pricingCalculation.discountCents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatUSD(pricingCalculation.finalCents)}</span>
                  </div>
                  <div className="flex justify-between text-birdie-charcoal/80">
                    <span>{taxLabel}</span>
                    <span>{formatUSD(taxCents)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lg border-t border-birdie-green/15 pt-2">
                    <span>Total:</span>
                    <span>{formatUSD(totalWithTax)}</span>
                  </div>
                  <div className="text-xs text-birdie-charcoal/60">
                    Deposit: {selectedService ? formatUSD(Number(selectedService.deposit_cents || 0)) : "$0"}
                  </div>
                </div>
              </div>
                );
              })()}
            </div>

            {/* Location & Notes */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium">Venue / Location</label>
                <div className="mt-1">
                  <VenueAutocomplete
                    value={locationAddress}
                    onChange={(v) => setLocationAddress(v)}
                    onSelect={({ combined }) => combined && setLocationAddress(combined)}
                    placeholder="Venue name or full address"
                  />
                </div>
              </div>
              <TaxCountyPicker
                address={locationAddress}
                county={taxCounty}
                rateBasisPoints={taxRateBasisPoints}
                autoDetected={taxAutoDetected}
                onChange={({ county, rateBasisPoints, autoDetected }) => {
                  setTaxCounty(county);
                  setTaxRateBasisPoints(rateBasisPoints);
                  setTaxAutoDetected(autoDetected);
                }}
              />
              <div>
                <label className="text-sm font-medium">Location Notes</label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                  rows={2}
                  value={locationNotes}
                  onChange={(e) => setLocationNotes(e.target.value)}
                  placeholder="Gate code, parking instructions, contact info..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Internal Notes</label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-birdie-green/15 px-3 py-2 text-sm focus:border-birdie-green focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40"
                  rows={2}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Private notes for your team..."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-birdie-green/15">
              <Button
                type="button"
                variant="secondary"
                onClick={() => onClose?.({ didCreate: false })}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                ← Back
              </Button>
              <Button type="submit" disabled={submitting || !selectedService}>
                {submitting ? "Creating..." : `Create Booking - ${formatUSD(pricingCalculation.finalCents)}`}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
